import maxBy from "lodash.maxby";

import {
  ConditionType,
  MarketRateScope,
  RateConditionType,
  RateGroupType,
  ShopifyCurrencyType,
  VariationDto,
  VariationFe,
} from "@intelligems/ig-types";
import { groupRounder } from "@intelligems/ig-utils";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { getVariationFeFromDto } from "@src/api/formatters/from-dto/variations";
import {
  initialShippingRateGroupsState,
  initialSingleVariationState,
  initialVariation,
  initialVariationState,
} from "@src/api/initials";
import {
  ShippingRateGroupReducer,
  ShippingRateGroupReducerAction,
} from "@src/api/reducers/shippingRateGroupReducer";
import { variationRateType } from "@src/features/experiments/pages/shippingExperience/components/RateBuilder/groupRates";
import { orderFlatThresholdRate } from "@src/features/experiments/pages/shippingExperience/components/RateBuilder/util";
import { IVariationsState } from "@src/store/types/variations";
import { sortByOrder } from "@src/utils/sortings";

const initialState: IVariationsState = {
  isSuccess: false,
  variations: [],
  storedVariations: [],
};

const slice = createSlice({
  name: "variations",
  initialState,
  reducers: {
    resetVariations: (_) => initialState,
    resetSingleVariationPriceChange: (draft) => {
      draft.variations[0].priceChange = undefined;
      draft.variations[0].priceChangeUnit = "percent";
    },
    /**
     * Reshape the CURRENT variations for a test <-> personalization switch,
     * in place, preserving the ids of variations that survive.
     *
     * Every variation-scoped slice — `redirects`, `onsiteEdits`,
     * `onsiteInjections`, `offers` — keys its rows by `variationId`. Rebuilding
     * the set from `initVariations({ variations: undefined })` mints fresh
     * `v4()` ids, which silently orphans all of that content: the rows stay in
     * state but are unreachable from every variation-scoped selector. Reshaping
     * here instead makes id preservation structural rather than something the
     * caller has to remember.
     *
     * New ids are minted only for genuinely new variations (the control added
     * when expanding a personalization into a test). Variations dropped by a
     * collapse do lose their id — the caller is responsible for clearing their
     * variation-scoped content, since a reducer can only see its own slice.
     */
    reshapeVariationsForMode: (
      draft,
      action: PayloadAction<{ isPersonalization: boolean }>
    ) => {
      const { isPersonalization } = action.payload;
      draft.isSuccess = true;

      if (draft.variations.length === 0) {
        draft.variations = isPersonalization
          ? [initialSingleVariationState()]
          : initialVariationState();
        draft.storedVariations = draft.variations;
        return;
      }

      if (isPersonalization) {
        // Collapse to the single 100% treatment. Keep the variation carrying
        // the payload — the first non-control group, since in a test the
        // control is by definition the untouched one — and adopt the
        // single-treatment shape around its existing id. Its name is left
        // alone: the merchant may have named it something meaningful.
        const payloadBearing =
          draft.variations.find((variation) => !variation.isControl) ??
          draft.variations[0];

        draft.variations = [
          {
            ...payloadBearing,
            isControl: true,
            order: 0,
            percentage: 100,
          },
        ];
        draft.storedVariations = draft.variations;
        return;
      }

      // Expanding a personalization into a test: the existing treatment becomes
      // the variant (keeping its id, and so its content), and a fresh control
      // is added alongside it. An experience that already has a control +
      // variant shape is left untouched.
      if (draft.variations.length === 1) {
        const [treatment] = draft.variations;
        const [control, defaultVariant] = initialVariationState();

        // A personalization's single treatment is named "Control Group" by
        // `initialSingleVariationState`. Carrying that name onto the variant
        // would leave two groups both called "Control Group", so fall back to
        // the default variant name — but only when the merchant never renamed
        // it. `label` mirrors `name` (see `initialFrontendProps`).
        const keepsOwnName = treatment.name !== control.name;

        draft.variations = [
          { ...control, percentage: 50, order: 0 },
          {
            ...treatment,
            isControl: false,
            order: 1,
            percentage: 50,
            ...(keepsOwnName
              ? {}
              : { name: defaultVariant.name, label: defaultVariant.name }),
          },
        ];
      }

      draft.storedVariations = draft.variations;
    },
    initVariations: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[] | undefined;
        isPersonalization?: boolean;
      }>
    ) => {
      const { variations, isPersonalization } = action.payload;

      draft.isSuccess = true;
      if (variations) {
        draft.variations = getVariationFeFromDto(variations);
      } else {
        if (isPersonalization) {
          draft.variations = [initialSingleVariationState()];
        } else {
          draft.variations = initialVariationState();
        }
      }
      draft.storedVariations = draft.variations;
    },
    setVariations: (draft, action: PayloadAction<VariationFe[]>) => {
      if (draft.variations) {
        // The control is never deletable, regardless of who's calling this
        // (AI path or otherwise) or whether the experience has started —
        // structural invariant of the data itself, so it belongs here rather
        // than in a caller-specific guard.
        const controlGroupVariation = draft.variations.find(
          (v) => v.isControl === true
        );
        if (
          controlGroupVariation &&
          !action.payload.some((v) => v.id === controlGroupVariation.id)
        ) {
          return;
        }
        draft.variations = action.payload;
      }
    },
    variationKeyChange: (
      draft,
      action: PayloadAction<{ index: number; value: string; key: string }>
    ) => {
      const { index, key, value } = action.payload;

      draft.variations[index][key] = value;
    },
    setTestExperienceId: (
      draft,
      action: PayloadAction<{ variationId: string; testExperienceId?: string }>
    ) => {
      const { variationId, testExperienceId } = action.payload;

      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation) {
        return;
      }

      variation.testExperienceId = testExperienceId;
    },
    toggleTierType: (
      draft,
      action: PayloadAction<{
        selectedTierType: RateConditionType;
        variationId: string;
      }>
    ) => {
      const { selectedTierType, variationId } = action.payload;

      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation) {
        return;
      }

      if (
        variation.shippingRateGroups &&
        variation.shippingRateGroups.length > 0
      ) {
        variation.shippingRateGroups.forEach((group) => {
          group.rates.forEach((rate) => {
            if (group.groupType !== "tieredByPriceOrWeight") {
              rate.condition = undefined;
            } else if (rate.condition) {
              rate.condition = {
                ...rate.condition,
                conditionType: selectedTierType,
              };
            }
          });
        });
      }
    },
    setAllRatesCurrency: (
      draft,
      action: PayloadAction<{ currency: string }>
    ) => {
      const { currency } = action.payload;

      for (const variation of draft.variations) {
        for (const group of variation.shippingRateGroups ?? []) {
          for (const rate of group.rates) {
            rate.currency = currency as ShopifyCurrencyType;
          }
        }
      }
    },
    dispatchShippingRateGroups: (
      draft,
      action: PayloadAction<{
        reducer: ShippingRateGroupReducerAction;
        variationIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }
      const { reducer, variationIndex } = action.payload;

      draft.variations[variationIndex] = ShippingRateGroupReducer(
        draft.variations[variationIndex],
        reducer
      )!;
    },
    initializeRateType: (
      draft,
      action: PayloadAction<{
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        variationId: string;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }

      const { variationId, shippingRateGroupType: type } = action.payload;
      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation) {
        return;
      }

      // Choosing a rate type restarts the whole variation with one shared
      // group; coverage is chosen per card afterwards.
      const restart = (target: VariationFe, groupType: RateGroupType) => {
        target.shippingRateGroups = [
          initialShippingRateGroupsState(
            groupType,
            action.payload.storeCurrencyCode,
            action.payload.tierType,
            action.payload.unitType,
            0,
            target.id
          ),
        ];
      };

      // Custom is test-wide: the first group chooses it for every group, and
      // leaving it there takes every group out of it.
      const [first, ...rest] = draft.variations;
      if (variation.id !== first.id) {
        if (variationRateType(first) === "custom") {
          restart(variation, "custom");
        } else if (type !== "custom") {
          restart(variation, type);
        }
        return;
      }
      const wasCustom = variationRateType(first) === "custom";
      restart(first, type);
      if (type === "custom" || wasCustom) {
        rest.forEach((other) => restart(other, type));
      }
    },
    addShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupType: RateGroupType;
        storeCurrencyCode: ShopifyCurrencyType;
        tierType: RateConditionType;
        unitType: ConditionType;
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }

      const variation = draft.variations.find(
        (v) => v.id === action.payload.variationId
      );
      if (!variation) {
        return;
      }

      // New groups start shared; coverage is chosen on the card afterwards.
      const groups = variation.shippingRateGroups!;
      const added = initialShippingRateGroupsState(
        action.payload.shippingRateGroupType,
        action.payload.storeCurrencyCode,
        action.payload.tierType,
        action.payload.unitType,
        groups.length,
        action.payload.variationId
      );
      // A tiered setup's free rate stays last: new bands go above it.
      const freeRateIndex =
        action.payload.shippingRateGroupType === "tieredByPriceOrWeight"
          ? groups.findIndex((group) => group.groupType === "thresholdOnly")
          : -1;
      if (freeRateIndex === -1) {
        groups.push(added);
      } else {
        groups.splice(freeRateIndex, 0, added);
        groups.forEach((group, index) => {
          group.position = index;
        });
      }
    },
    setShippingRateGroupScopes: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupIndexes: number[];
        scopes: MarketRateScope[];
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }
      const variation = draft.variations.find(
        (v) => v.id === action.payload.variationId
      );
      if (!variation?.shippingRateGroups) {
        return;
      }
      for (const index of action.payload.shippingRateGroupIndexes) {
        const group = variation.shippingRateGroups[index];
        if (group) {
          group.scopes = action.payload.scopes;
        }
      }
    },
    removeShippingRate: (
      draft,
      action: PayloadAction<{
        variationId: string;
        shippingRateGroupIndex: number;
      }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }

      const { variationId, shippingRateGroupIndex } = action.payload;

      const variation = draft.variations.find((v) => v.id === variationId);
      if (!variation?.shippingRateGroups) {
        return;
      }

      variation.shippingRateGroups = variation.shippingRateGroups
        .filter((_, index) => index !== shippingRateGroupIndex)
        .map((group, index) => ({ ...group, position: index }));
    },
    deleteVariation: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) {
        return;
      }

      draft.variations = groupRounder(
        draft.variations
          .filter((v) => v.id !== action.payload)
          .map((v, index) => ({
            ...v,
            order: index,
            percentage: 100 / (draft.variations.length - 1),
          }))
      );
    },
    addVariation: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) {
        return;
      }
      const id = action.payload;

      const maxOrder = maxBy(draft.variations, "order")?.order || 0;
      draft.variations.push(initialVariation(id, maxOrder, draft.variations));
      draft.variations.forEach((v) => {
        v.percentage = 100 / draft.variations.length;
      });
      draft.variations = groupRounder(draft.variations);
    },
    reorderVariations: (
      draft,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      if (!draft.isSuccess) {
        return;
      }

      const [removed] = draft.variations.splice(action.payload.startIndex, 1);
      draft.variations.splice(action.payload.endIndex, 0, removed);
      draft.variations.forEach((v, index) => (v.order = index));
      draft.variations = draft.variations.sort(sortByOrder);
    },
    variationSlide: (draft, action: PayloadAction<Record<string, number>>) => {
      if (!draft.isSuccess) {
        return draft;
      }

      for (const variation of draft.variations) {
        variation.percentage = action.payload[variation.id] ?? 0;
      }
    },
    ensureRateGroupOrder: (draft) => {
      if (!draft.isSuccess) {
        return;
      }

      draft.variations.forEach((variation) => {
        if (variation.shippingRateGroups) {
          variation.shippingRateGroups.forEach((rateGroup) =>
            rateGroup.rates.sort(orderFlatThresholdRate)
          );
        }
      });
    },
  },
  selectors: {
    selectedVariationsState: (state) => state,
    selectedVariations: (state) => state.variations || [],
    getControlGroup: (state) => state.variations?.find((v) => v.isControl),
  },
});

export const {
  resetVariations,
  initVariations,
  reshapeVariationsForMode,
  setVariations,
  dispatchShippingRateGroups,
  initializeRateType,
  removeShippingRate,
  addVariation,
  addShippingRate,
  setShippingRateGroupScopes,
  toggleTierType,
  deleteVariation,
  variationKeyChange,
  variationSlide,
  reorderVariations,
  ensureRateGroupOrder,
  setTestExperienceId,
  resetSingleVariationPriceChange,
  setAllRatesCurrency,
} = slice.actions;

export const variationsReducer = slice.reducer;

export const { selectedVariationsState, selectedVariations, getControlGroup } =
  slice.selectors;
